import React, { useEffect, useRef, useState, useCallback } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";

function useDebouncer(cb, delay = 350) {
  const tRef = useRef(null);
  const call = useCallback(
    (...args) => {
      clearTimeout(tRef.current);
      tRef.current = setTimeout(() => cb(...args), delay);
    },
    [cb, delay],
  );
  const cancel = useCallback(() => clearTimeout(tRef.current), []);
  return [call, cancel];
}

export default function FiltroVehiculosConTarjeta({
  initialFilters = {},
  onSubmit = () => {},
  onClose = () => {},
  onChangeFilters = null,
}) {
  const filtroRef = useRef(null);
  const marListRef = useRef(null);
  const AC_URL =
    apiRoutes?.vehiculosConTarjeta?.autocomplete ||
    apiRoutes?.vehiculo?.autocomplete ||
    "/flpa/api/vehiculo/autocomplete";

  const min2 = (s) => (s || "").trim().length >= 2;
  const closeDropdowns = (except = null) => {
    if (except !== "cliente") setCliOpen(false);
    if (except !== "patente") setPatOpen(false);
    if (except !== "desc") setDesOpen(false);
    if (except !== "marca") setMarOpen(false);
  };

  // ====== Chips / selected ======
  const [clienteSel, setClienteSel] = useState(initialFilters?.cliente ?? null);
  const [patenteSel, setPatenteSel] = useState(
    initialFilters?.patente ? String(initialFilters.patente) : "",
  );
  const [descSel, setDescSel] = useState(initialFilters?.descripcion ?? ""); // texto visible
  const [descKeySel, setDescKeySel] = useState(
    initialFilters?.descripcionKey ?? "",
  ); // CLAVE normalizada
  // compat (no usado ya):
  const [vehiculoIdsSel, setVehiculoIdsSel] = useState(
    initialFilters?.vehiculoIds ?? null,
  );

  // guardamos la selección efectiva en una ref para evitar carreras
  const descPickRef = useRef(
    initialFilters?.descripcionKey
      ? {
          value: initialFilters?.descripcion ?? "",
          key: initialFilters.descripcionKey,
        }
      : null,
  );

  const [marcaSel, setMarcaSel] = useState(
    initialFilters?.marca?.id
      ? initialFilters.marca
      : initialFilters?.marcaId
        ? {
            id: initialFilters.marcaId,
            nombre: initialFilters.marcaNombre ?? "",
          }
        : null,
  );

  // ====== Inputs (siempre vacíos) ======
  const [clienteQ, setClienteQ] = useState("");
  const [patenteQ, setPatenteQ] = useState("");
  const [descQ, setDescQ] = useState("");

  // ====== Dropdowns ======
  const [cliItems, setCliItems] = useState([]);
  const [cliPage, setCliPage] = useState(0);
  const [cliHasMore, setCliHasMore] = useState(false);
  const [cliOpen, setCliOpen] = useState(false);
  const [cliLoading, setCliLoading] = useState(false);

  const [patItems, setPatItems] = useState([]);
  const [patPage, setPatPage] = useState(0);
  const [patHasMore, setPatHasMore] = useState(false);
  const [patOpen, setPatOpen] = useState(false);
  const [patLoading, setPatLoading] = useState(false);

  const [desItems, setDesItems] = useState([]);
  const [desPage, setDesPage] = useState(0);
  const [desHasMore, setDesHasMore] = useState(false);
  const [desOpen, setDesOpen] = useState(false);
  const [desLoading, setDesLoading] = useState(false);

  const [marItems, setMarItems] = useState([]);
  const [marPage, setMarPage] = useState(0);
  const [marHasMore, setMarHasMore] = useState(true);
  const [marOpen, setMarOpen] = useState(false);
  const [marLoading, setMarLoading] = useState(false);
  const MAR_SIZE = 30;

  // ====== Rehidratación ======
  useEffect(() => {
    setClienteSel(initialFilters?.cliente ?? null);
    setPatenteSel(
      initialFilters?.patente ? String(initialFilters.patente) : "",
    );
    setDescSel(initialFilters?.descripcion ?? "");
    setDescKeySel(initialFilters?.descripcionKey ?? "");
    setVehiculoIdsSel(initialFilters?.vehiculoIds ?? null); // compat (no usamos)
    descPickRef.current = initialFilters?.descripcionKey
      ? {
          value: initialFilters?.descripcion ?? "",
          key: initialFilters.descripcionKey,
        }
      : null;
    setMarcaSel(
      initialFilters?.marca?.id
        ? initialFilters.marca
        : initialFilters?.marcaId
          ? {
              id: initialFilters.marcaId,
              nombre: initialFilters.marcaNombre ?? "",
            }
          : null,
    );
  }, [initialFilters]);

  // ====== Cerrar al click afuera / ESC ======
  useEffect(() => {
    const outside = (e) => {
      if (filtroRef.current && !filtroRef.current.contains(e.target)) onClose();
    };
    const esc = (e) => e.key === "Escape" && closeDropdowns(null);
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  // ====== Fetch genérico ======
  const fetchAuto = useCallback(
    async (tipo, q, page = 0, size = 10) => {
      const p = new URLSearchParams({ tipo, q: q ?? "", page, size });
      return apiFetchJson(`${AC_URL}?${p.toString()}`);
    },
    [AC_URL],
  );

  // ====== Cliente ======
  const doCliSearch = useCallback(
    async (term, page = 0, append = false) => {
      if (!min2(term)) {
        setCliItems([]);
        setCliOpen(false);
        setCliHasMore(false);
        setCliPage(0);
        return;
      }
      setCliLoading(true);
      try {
        const j = await fetchAuto("cliente", term, page, 15);
        const raws = Array.isArray(j?.items) ? j.items : [];
        const mapped = raws.map((r) => ({
          id: r.id ?? r.clienteId ?? null,
          codigo: r.codigo ?? r.clienteCodigo ?? "",
          nombre: r.nombre ?? r.clienteNombre ?? "",
        }));
        setCliItems((prev) => (append ? [...prev, ...mapped] : mapped));
        setCliHasMore(Boolean(j?.hasMore));
        setCliPage(page);
        setCliOpen(true);
      } finally {
        setCliLoading(false);
      }
    },
    [fetchAuto],
  );
  const [debCli, cancelDebCli] = useDebouncer(doCliSearch, 350);

  useEffect(() => {
    if (min2(clienteQ)) debCli(clienteQ, 0, false);
    else {
      cancelDebCli();
      setCliItems([]);
      setCliOpen(false);
      setCliHasMore(false);
      setCliPage(0);
    }
  }, [clienteQ]); // eslint-disable-line

  const loadMoreCli = async () => {
    if (!cliLoading && cliHasMore)
      await doCliSearch(clienteQ, cliPage + 1, true);
  };

  // ====== Patente ======
  const doPatSearch = useCallback(
    async (term, page = 0, append = false) => {
      if (!min2(term)) {
        setPatItems([]);
        setPatOpen(false);
        setPatHasMore(false);
        setPatPage(0);
        return;
      }
      setPatLoading(true);
      try {
        const j = await fetchAuto("patente", term, page, 15);
        const items = Array.isArray(j?.items) ? j.items : [];
        const mapped = items.map((r) => ({
          value: r.patente ?? r.value ?? "",
        }));
        setPatItems((prev) => (append ? [...prev, ...mapped] : mapped));
        setPatHasMore(Boolean(j?.hasMore));
        setPatPage(page);
        setPatOpen(true);
      } finally {
        setPatLoading(false);
      }
    },
    [fetchAuto],
  );
  const [debPat, cancelDebPat] = useDebouncer(doPatSearch, 350);

  useEffect(() => {
    if (min2(patenteQ)) debPat(patenteQ, 0, false);
    else {
      cancelDebPat();
      setPatItems([]);
      setPatOpen(false);
      setPatHasMore(false);
      setPatPage(0);
    }
  }, [patenteQ]); // eslint-disable-line

  const loadMorePat = async () => {
    if (!patLoading && patHasMore)
      await doPatSearch(patenteQ, patPage + 1, true);
  };

  // ====== Descripción/Modelo (AGRUPADO) ======
  const doDescSearch = useCallback(
    async (term, page = 0, append = false) => {
      if (!min2(term)) {
        setDesItems([]);
        setDesOpen(false);
        setDesHasMore(false);
        setDesPage(0);
        return;
      }
      setDesLoading(true);
      try {
        const j = await fetchAuto("descripcion", term, page, 15);
        const items = Array.isArray(j?.items) ? j.items : [];
        // backend: { descripcionModelo, descripcionKey, count }
        const mapped = items.map((r) => {
          const texto = r.descripcionModelo ?? r.value ?? "";
          const cant = r.count ?? undefined;
          return {
            label: cant ? `${texto} (${cant})` : texto,
            value: texto,
            key: r.descripcionKey ?? "", // CLAVE
          };
        });
        setDesItems((prev) => (append ? [...prev, ...mapped] : mapped));
        setDesHasMore(Boolean(j?.hasMore));
        setDesPage(page);
        setDesOpen(true);
      } finally {
        setDesLoading(false);
      }
    },
    [fetchAuto],
  );
  const [debDesc, cancelDebDesc] = useDebouncer(doDescSearch, 350);

  useEffect(() => {
    if (min2(descQ)) debDesc(descQ, 0, false);
    else {
      cancelDebDesc();
      setDesItems([]);
      setDesOpen(false);
      setDesHasMore(false);
      setDesPage(0);
    }
  }, [descQ]); // eslint-disable-line

  const loadMoreDes = async () => {
    if (!desLoading && desHasMore) await doDescSearch(descQ, desPage + 1, true);
  };

  // ====== Marcas ======
  const ensureMarOpen = async () => {
    closeDropdowns("marca");

    setMarOpen((wasOpen) => {
      const opening = !wasOpen;

      if (opening) {
        // Si hay selección y aún no está en la lista, la añadimos una vez (sin duplicar)
        if (marcaSel) {
          setMarItems((old) => {
            const exists = old.some((m) => m.id === marcaSel.id);
            return exists ? old : [marcaSel, ...old];
          });
        }

        // Si la lista está vacía, disparar la primera carga
        if (marItems.length === 0 && marHasMore && !marLoading) {
          // no await aquí, solo dispara
          loadMoreMar();
        }

        // Esperar a que el panel renderice y scrollear hasta la seleccionada
        setTimeout(() => {
          if (!marcaSel) return;
          const container = marListRef.current;
          if (!container) return;
          const el = container.querySelector(
            `[data-marca-id="${marcaSel.id}"]`,
          );
          if (el) el.scrollIntoView({ block: "nearest" });
        }, 0);
      }

      return !wasOpen;
    });
  };

  const loadMoreMar = async () => {
    if (marLoading || !marHasMore) return;
    setMarLoading(true);
    try {
      const j = await fetchAuto("marca", "", marPage, MAR_SIZE);
      const items = Array.isArray(j?.items) ? j.items : [];
      const norm = items.map((m) => ({
        id: m.id ?? m.value ?? m.codigo ?? m.marcaId,
        nombre: m.nombre ?? m.value ?? m.marcaNombre ?? m.descripcion ?? "",
      }));
      setMarItems((old) => {
        const merged = [...old];
        for (const n of norm) {
          if (!merged.some((m) => m.id === n.id)) merged.push(n);
        }
        return merged;
      });
      setMarHasMore(Boolean(j?.hasMore));
      setMarPage((p) => p + 1);
    } finally {
      setMarLoading(false);
    }
  };

  const pickMarca = (m) => {
    setMarcaSel(m);
    setMarOpen(false);
    emitChange(
      buildSnapshot({
        marca: m,
        marcaId: m?.id ?? null,
        marcaNombre: m?.nombre ?? "",
      }),
    );
  };
  const clearMarca = () => {
    setMarcaSel(null);
    setMarOpen(false);
    emitChange(buildSnapshot({ marca: null, marcaId: null, marcaNombre: "" }));
  };

  // ====== Emisión ======
  const emitChange = (next) => {
    if (typeof onChangeFilters === "function") onChangeFilters(next);
  };

  // ====== Selecciones ======
  const pickCliente = (it) => {
    cancelDebCli();
    setClienteSel({ id: it.id, nombre: it.nombre, codigo: it.codigo });
    setClienteQ("");
    setCliItems([]);
    setCliOpen(false);
    setCliHasMore(false);
    setCliPage(0);
    emitChange(
      buildSnapshot({
        cliente: { id: it.id, nombre: it.nombre, codigo: it.codigo },
        clienteNombre: it.nombre,
        clienteId: it.id,
      }),
    );
  };
  const removeCliente = () => {
    setClienteSel(null);
    emitChange(
      buildSnapshot({ cliente: null, clienteNombre: "", clienteId: null }),
    );
  };

  const pickPatente = (value) => {
    cancelDebPat();
    setPatenteSel(value);
    setPatenteQ("");
    setPatItems([]);
    setPatOpen(false);
    setPatHasMore(false);
    setPatPage(0);
    emitChange(buildSnapshot({ patente: value }));
  };
  const removePatente = () => {
    setPatenteSel("");
    emitChange(buildSnapshot({ patente: "" }));
  };

  const pickDesc = (it) => {
    cancelDebDesc();
    setDescSel(it.value);
    setDescKeySel(it.key || "");
    setVehiculoIdsSel(null); // compat: ya no usamos IDs
    descPickRef.current = { value: it.value, key: it.key || "" };
    setDescQ("");
    setDesItems([]);
    setDesOpen(false);
    setDesHasMore(false);
    setDesPage(0);
    emitChange(
      buildSnapshot({
        descripcion: it.value,
        descripcionKey: it.key || "",
        vehiculoIds: null,
      }),
    );
  };
  const removeDesc = () => {
    setDescSel("");
    setDescKeySel("");
    setVehiculoIdsSel(null);
    descPickRef.current = null;
    emitChange(
      buildSnapshot({ descripcion: "", descripcionKey: "", vehiculoIds: null }),
    );
  };

  // ====== Submit / snapshot ======
  const buildSnapshot = (overrides = {}) => {
    const keyFromRef = descPickRef.current?.key || "";
    const snap = {
      cliente: clienteSel,
      clienteNombre: clienteSel ? (clienteSel.nombre ?? "") : clienteQ || "",
      clienteId: clienteSel?.id ?? null,

      patente: (patenteSel || patenteQ || "").trim(),
      descripcion: (descSel || descQ || "").trim(),

      descripcionKey: keyFromRef || (descKeySel || "").trim(), // CLAVE QUE SE ENVÍA
      vehiculoIds: null, // compat, forzado a null

      marca: marcaSel,
      marcaId: marcaSel?.id ?? null,
      marcaNombre: marcaSel?.nombre ?? "",
    };
    return { ...snap, ...overrides };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(buildSnapshot()); // aplica filtros con descripcionKey
    onClose(); // cierra el panel
  };

  // ====== Render ======
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
      <div
        ref={filtroRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white shadow-xl p-6 overflow-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-title">Filtros</h2>
          <button
            onClick={onClose}
            className="btn-cancel"
            title="Cerrar"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
          {/* Cliente */}
          <div>
            <label className="form-label-fil">Cliente</label>

            <ClienteAutocomplete
              value={clienteSel}
              onChange={(cli) => {
                // cli puede ser null o { id, nombre, codigo }
                setClienteSel(cli);

                emitChange(
                  buildSnapshot({
                    cliente: cli,
                    clienteId: cli?.id ?? null,
                    clienteNombre: cli?.nombre ?? "",
                  })
                );
              }}
              placeholder="Escribí al menos 2 caracteres..."
            />

            {clienteSel && (
              <div className="mb-1 inline-flex items-center gap-2 text-gray px-2 py-1 rounded-full">
                <span>
                  {clienteSel.codigo
                    ? `${clienteSel.codigo} — `
                    : ""}
                  {clienteSel.nombre}
                </span>
                <button type="button" onClick={() => {
                  setClienteSel(null);
                  emitChange(
                    buildSnapshot({
                      cliente: null,
                      clienteId: null,
                      clienteNombre: "",
                    })
                  );
                }}>
                  ×
                </button>
              </div>
            )}
</div>


          {/* Patente */}
          <div style={{ position: "relative" }}>
            <label className="form-label-fil">
              N° de chapa
            </label>
            <input
              type="text"
              placeholder="ABC123 o similar..."
              value={patenteQ}
              onChange={(e) => {
                setPatenteQ(e.target.value);
                closeDropdowns("patente");
              }}
              className="input"
              autoComplete="off"
              onFocus={() => {
                closeDropdowns("patente");
                if (min2(patenteQ) && patItems.length) setPatOpen(true);
              }}
            />
            {patenteSel && (
              <div className="mb-1 inline-flex items-center gap-2  text-gray px-2 py-1 rounded-full">
                <span>{patenteSel}</span>
                <button type="button" onClick={removePatente} title="Quitar">
                  ×
                </button>
              </div>
            )}

            {patOpen && (
              <div
                className="dropdown-panel"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24)
                    loadMorePat();
                }}
              >
                {patItems.map((it, idx) => (
                  <div
                    key={`${it.value ?? idx}-${idx}`}
                    className="dropdown-item"
                    onClick={() => pickPatente(it.value)}
                  >
                    {it.value}
                  </div>
                ))}
                {patLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Cargando…
                  </div>
                )}
                {!patLoading &&
                  !patHasMore &&
                  patItems.length === 0 &&
                  min2(patenteQ) && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Sin coincidencias
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Descripción/Modelo */}
          <div style={{ position: "relative" }}>
            <label className="form-label-fil">
              Descripción/Modelo
            </label>
            <textarea
              rows={2}
              placeholder="Descripción, modelo o texto libre..."
              value={descQ}
              onChange={(e) => {
                setDescQ(e.target.value);
                closeDropdowns("desc");
              }}
              className="input rounded-2xl"
              onFocus={() => {
                closeDropdowns("desc");
                if (min2(descQ) && desItems.length) setDesOpen(true);
              }}
            />
            {descSel && (
              <div className="mb-1 inline-flex items-center gap-2  text-gray px-2 py-1 rounded-full">
                <span>{descSel}</span>
                <button type="button" onClick={removeDesc} title="Quitar">
                  ×
                </button>
              </div>
            )}

            {desOpen && (
              <div
                className="dropdown-panel"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24)
                    loadMoreDes();
                }}
              >
                {desItems.map((it, idx) => (
                  <div
                    key={`${it.value ?? idx}-${idx}`}
                    className="dropdown-item"
                    onClick={() => pickDesc(it)}
                  >
                    {it.label}
                  </div>
                ))}
                {desLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Cargando…
                  </div>
                )}
                {!desLoading &&
                  !desHasMore &&
                  desItems.length === 0 &&
                  min2(descQ) && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Sin coincidencias
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Marca */}
          <div style={{ position: "relative" }}>
            <label className="form-label-fil">
              Marca
            </label>
            <div
              className="input flex items-center justify-between cursor-pointer"
              onClick={ensureMarOpen}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && ensureMarOpen()
              }
            >
              <span className="truncate">
                {marcaSel
                  ? (marcaSel.nombre ?? marcaSel.value ?? marcaSel.id)
                  : "Todas"}
              </span>
              <span className="ml-2">▾</span>
            </div>
            {marOpen && (
              <div
                className="dropdown-panel"
                ref={marListRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24)
                    loadMoreMar();
                }}
              >
                <div className="dropdown-item" onClick={clearMarca}>
                  Todas
                </div>

                {marItems.map((m) => (
                  <div
                    key={m.id}
                    data-marca-id={m.id}
                    className={`dropdown-item ${marcaSel && m.id === marcaSel.id ? "bg-green-100 font-medium" : ""}`}
                    onClick={() => pickMarca(m)}
                  >
                    {m.nombre ?? m.value ?? m.id}
                    {marcaSel && m.id === marcaSel.id ? (
                      <span className="ml-2">✓</span>
                    ) : null}
                  </div>
                ))}

                {marLoading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Cargando…
                  </div>
                )}
                {!marLoading && !marHasMore && marItems.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Sin marcas
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
